/**
 * Labstep
 *
 * @module grid/SmartTableProtocolValue
 * @desc DataGrid for SmartTable ProtocolValue
 */

import type { AgGridReact } from 'ag-grid-react';
import { DataGrid } from 'labstep-web/core/DataGrid';
import { LoaderWithProgress } from 'labstep-web/core/DataGrid/Overlay/LoaderProgress';
import type { IDataGridProps } from 'labstep-web/core/DataGrid/types';
import { useExportProgressContext } from 'labstep-web/grid/Index/components/DataGridExportAsCSV/context';
import { GridSmartTableColumnHeader } from 'labstep-web/grid/SmartTableProtocolValue/components/ColumnHeader';
import { GridSmartTableStatusBar } from 'labstep-web/grid/SmartTableProtocolValue/components/StatusBar';
import ToolPanel from 'labstep-web/grid/SmartTableProtocolValue/components/ToolPanel';
import { useBulkSelect } from 'labstep-web/grid/SmartTableProtocolValue/hooks';
import { GridSmartTableProtocolValueService } from 'labstep-web/grid/SmartTableProtocolValue/services/grid-smart-table-protocol-value.service';
import { GridSmartTableProtocolValueColDefService } from 'labstep-web/grid/SmartTableProtocolValue/services/grid-smart-table-protocol-value-coldef.service';
import { GridService } from 'labstep-web/grid/services/grid.service';
import type { ProtocolValue } from 'labstep-web/models/protocol-value.model';
import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import styles from './styles.module.scss';
import type { GridSmartTableProps } from './types';

// Define the cell state interface
export type CellState = 'loading' | 'error' | 'success';

// Define the grid context interface
interface GridContext {
  cellStates: {
    [entityId: string]: {
      [colId: string]: CellState;
    };
  };
  setCellState: (
    entityId: string,
    colId: string,
    state: CellState,
  ) => void;
  getCellState: (entityId: string, colId: string) => CellState | null;
}

export const GridSmartTable: React.FC<GridSmartTableProps> = ({
  experimentOrProtocol,
  entities,
  entityView,
  fullscreen = false,
  ...rest
}) => {
  // Store grid API reference
  const [gridApi, setGridApi] = useState<any>(null);

  // Bulk select on row selection
  const bulkSelectProps = useBulkSelect();

  // Set column header custom component
  const components = useMemo(
    () => ({
      agColumnHeader: GridSmartTableColumnHeader,
    }),
    [],
  );

  // Cell state management
  const [cellStates, setCellStates] = useState<
    GridContext['cellStates']
  >({});

  // Grid context with cell state management functions
  const gridContext: GridContext = useMemo(
    () => ({
      cellStates,
      setCellState: (
        entityId: string,
        colId: string,
        state: CellState | null,
      ) => {
        setCellStates((prev) => {
          if (state === null) {
            // Remove the key when state is null
            const { [colId]: _, ...restCols } = prev[entityId] || {};
            const updatedEntity = { ...restCols };

            // If no columns left for this entity, remove the entity entirely
            if (Object.keys(updatedEntity).length === 0) {
              const { [entityId]: __, ...restEntities } = prev;
              return restEntities;
            }

            return {
              ...prev,
              [entityId]: updatedEntity,
            };
          }

          // Set the state normally
          return {
            ...prev,
            [entityId]: {
              ...prev[entityId],
              [colId]: state,
            },
          };
        });
      },
      getCellState: (entityId: string, colId: string) => {
        return cellStates[entityId]?.[colId] || null;
      },
    }),
    [cellStates],
  );

  // Get the row id from the idAttr field - Required for AG Grid
  const getRowId = useCallback<
    NonNullable<IDataGridProps['getRowId']>
  >((params) => params.data.idAttr, []);

  const exportProgressContext = useExportProgressContext();

  useEffect(() => {
    if (exportProgressContext) {
      exportProgressContext.setExportProgress((prev) => ({
        ...prev,
        initialEntitiesCount: entities.length,
      }));
    }
  }, [entities.length]);

  // Update sidebar when entityView.parameters changes
  useEffect(() => {
    if (gridApi) {
      const sideBar = {
        toolPanels: [
          {
            id: 'columns',
            labelDefault: 'Columns',
            labelKey: 'customs',
            iconKey: 'columns',
            toolPanel: ToolPanel,
            toolPanelParams: {
              entityView,
              experimentOrProtocol,
              fullscreen,
            },
          },
        ],
        defaultToolPanel: 'columns',
      };
      const isSideBarVisible = gridApi.isSideBarVisible();
      gridApi.setSideBar(sideBar);
      gridApi.setSideBarVisible(isSideBarVisible);
    }
  }, [entityView.parameters]);

  const statusBarChildren = useMemo(() => {
    return GridSmartTableProtocolValueService.getStatusBarChildren({
      experimentOrProtocol,
    });
  }, [experimentOrProtocol]);

  const statusBar = useMemo(() => {
    return {
      statusPanels: [
        {
          statusPanel: GridSmartTableStatusBar,
          statusPanelParams: {
            children: statusBarChildren,
          },
          align: 'left',
        },
      ],
    };
  }, [statusBarChildren]);

  const protocolValueNameFilter =
    GridSmartTableProtocolValueService.getEntityViewProtocolValueNameFilter(
      entityView,
    );
  const selectedProtocolValues = protocolValueNameFilter
    ? entities.filter((protocolValue: ProtocolValue) =>
        ((protocolValueNameFilter.value as string[]) || []).includes(
          protocolValue.reportingName,
        ),
      )
    : [];

  return (
    <DataGrid
      key={entityView?.column_definition_ids?.join('-')}
      height={'100%'}
      width={'100%'}
      columnDefs={GridSmartTableProtocolValueColDefService.getColDefs(
        entityView,
        experimentOrProtocol,
        fullscreen,
      )}
      getRowId={getRowId}
      rowData={selectedProtocolValues}
      components={components}
      context={gridContext}
      {...bulkSelectProps}
      onColumnResized={(params) => {
        const gridService = new GridService(
          params as unknown as AgGridReact,
        );
        entityView.state = gridService.getState();
        gridService.setState(entityView.state);
      }}
      onGridReady={(params): void => {
        const gridService = new GridService(
          params as unknown as AgGridReact,
        );
        gridService.setState(entityView.state);

        // Store the grid API reference
        setGridApi(params.api);

        const sideBar = {
          toolPanels: [
            {
              id: 'columns',
              labelDefault: 'Columns',
              labelKey: 'customs',
              iconKey: 'columns',
              toolPanel: ToolPanel,
              toolPanelParams: {
                entityView,
                experimentOrProtocol,
                fullscreen,
              },
            },
          ],
          defaultToolPanel: 'columns',
        };
        if (params?.api) {
          const isSideBarVisible = params.api.isSideBarVisible();
          params.api.setSideBar(sideBar);
          params.api.setSideBarVisible(isSideBarVisible);
        }
      }}
      statusBar={statusBar}
      {...rest}
      progressComponent={
        exportProgressContext &&
        exportProgressContext.exportProgress.isExporting && (
          <LoaderWithProgress
            value={exportProgressContext.exportProgress.value}
            total={exportProgressContext.exportProgress.total}
            hasFailed={exportProgressContext.exportProgress.hasFailed}
          />
        )
      }
      hidden={false}
      className={fullscreen ? styles.fullscreen : styles.inline}
    />
  );
};
